import React, {FC, ReactNode, useEffect, useState} from 'react';
import {FullPageSpinner} from 'components/FullPageSpinner';
import {ReloadError} from 'components/ReloadError';
import {useIsClient} from 'hooks/useIsClient';
import {usePermissions} from 'hooks/usePermissions';

import {useCreateProvisional} from './mutation.create';

interface ProvisionalProps {
	children: ReactNode;
}

const ProvisionalPage: FC<ProvisionalProps> = ({children}) => {
	const {auth, loading} = usePermissions();
	const {isClient} = useIsClient();
	const [error, setError] = useState<string>('');

	// This is the mutation used to automatically create a
	// provisional user if we're not already logged in
	const [create] = useCreateProvisional({
		onCompleted: async (data) => {
			const {token} = data.provisional.create;
			if (token) {
				await auth.setToken(token).catch(
					(e) => {
						// We expect to sometimes issue a reset while
						// there may be other queries in flight.
						//
						// This is expected so catch & log; don't panic.
						// eslint-disable-next-line no-console
						console.log('store reset error:', e);
					},
				);
			} else {
				setError(data.provisional.create.error || '');
			}
		},
		onError: () => {
			setError('Could not contact server.');
		},
	});

	// Check if logged in; log in as provisional user if not
	useEffect(() => {
		if (!loading && !auth.isLoggedIn) {
			create();
		}
	}, [
		auth.isLoggedIn,
		loading,
	]);

	// If not logged in, return a spinner (or error screen)
	if (!auth.isLoggedIn || !isClient) {
		if (error) {
			return <ReloadError fullPage />;
		}
		return <FullPageSpinner />;
	}

	return <>{children}</>;
};

export default ProvisionalPage;
